<template>
  <div v-once>
    <section class="section section-hero">
      <div class="row align-middle align-justify">
        <div class="columns small-12 medium-6 large-6">
          <h1>What's Buzzing Magazine - {{ region }}</h1>
          <p class="text-big">
            Here at Teach Starter, we understand the time and energy required to organise engaging
            and authentic literacy activities for your students. Let us take the stress out of your
            literacy planning with our beautifully designed and highly informative magazine!
          </p>
        </div>

        <div class="columns small-12 medium-5 large-5">
          <nuxt-img
            src="/images/landing/magazine/whats-buzzing-content-1.png"
            alt=""
            height="422"
            width="600"
          />
        </div>
      </div>
    </section>

    <section
      v-for="issue in issues"
      :key="issue.name"
      class="section section-callout text-center section-issues"
    >
      <header class="row">
        <div class="columns small-12">
          <h2>{{ issue.name }} ({{ issue.year }})</h2>
          <div>{{ issue.description }}</div>
        </div>
      </header>
      <div class="row align-middle">
        <div class="columns small-12">
          <ul class="row block-grid small-up-2 medium-up-3 large-up-4">
            <li v-for="year in issue.years" :key="year.slug">
              <nuxt-link :to="year.link" title="Download resource" class="shadow">
                <nuxt-img
                  :src="getImageUrl(issue.slug, year.slug)"
                  alt=""
                  height="261"
                  width="200"
                />
              </nuxt-link>

              <h3>{{ year.name }}</h3>
              <p>
                <nuxt-link :to="year.link" title="Download resource">Download</nuxt-link>
              </p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="row section align-center align-middle">
      <div class="columns small-12 medium-6 medium-order-2">
        <h2>Relevant, Age-Appropriate Content</h2>
        <p>
          Teach Starter’s experienced team of educators have curated the magazine’s content
          specifically for students of appropriate Year levels. The magazine uses age-appropriate
          vocabulary to address a wide range of relevant and engaging topics.
        </p>
      </div>

      <div class="columns small-12 medium-6 medium-order-1">
        <nuxt-img
          src="/images/landing/magazine/whats-buzzing-content-2.png"
          alt=""
          height="476"
          width="600"
        />
      </div>
    </section>

    <section class="row section align-center align-middle">
      <div class="columns small-12 medium-6">
        <h2>Curriculum-Aligned Articles</h2>
        <p>
          Integrating literacy across the curriculum has never been easier! The articles contained
          within the magazine directly relate to a range of learning areas such as Science, HASS,
          The Arts, and Health and Physical Education.
        </p>
      </div>

      <div class="columns small-12 medium-6">
        <nuxt-img
          src="/images/landing/magazine/whats-buzzing-content-3.png"
          alt=""
          height="422"
          width="600"
        />
      </div>
    </section>

    <section class="row section align-center align-middle">
      <div class="columns small-12 medium-6 medium-order-2">
        <h2>Accompanying Activities for Students</h2>
        <p>
          The magazine will be accompanied by five sets of task cards addressing the areas of
          writing, language, comprehension, reading strategies and higher-order thinking skills.
          This amazing teaching resource will be available for all Teach Starter members with a paid
          subscription.
        </p>
      </div>

      <div class="columns small-12 medium-6 medium-order-1">
        <nuxt-img
          src="/images/landing/magazine/whats-buzzing-content-4.png"
          alt=""
          height="600"
          width="600"
        />
      </div>
    </section>

    <section class="row section align-center align-middle">
      <div class="columns small-12 medium-6">
        <h2>
          Differentiation:
          <span>The Magazine Advantage</span>
        </h2>
        <p>
          When it comes to differentiating for individual students, magazines have many advantages
          over more traditional texts. Magazines include multiple genres, varied text lengths, a
          diverse range of topics and assorted layouts. There really is something for everyone!
        </p>
      </div>

      <div class="columns small-12 medium-6">
        <nuxt-img
          src="/images/landing/magazine/whats-buzzing-content-5.jpg"
          alt=""
          width="684"
          height="600"
        />
      </div>
    </section>
  </div>
</template>

<script>
/* What's Buzzing landing page
 *
 * @parent:
 * @requests:
 * @events:
 * @props:
 * @components:
 * @methods:
 */

import issues from '~/data/whats_buzzing.json';

export default {
  name: 'WhatsBuzzingIndex',
  data() {
    return {
      title: "What's Buzzing Magazine",
      description: 'Get the latest issues of What’s Buzzing magazine for your class',
      thisYear: new Date().getFullYear(),
      issues: issues[this.$i18n.locale].filter(i => i.name !== 'Bonus Issue'),
      region: this.$i18n ? this.$i18n.t('name') : null,
      root: '/' + this.$i18n.locale + '/whats-buzzing/',
    };
  },
  jsonld() {
    return this.$jsonSchemaPage(this.title, this.$route.path);
  },
  head() {
    return {
      title: this.title,
      meta: [
        { hid: 'title', name: 'title', content: this.title },
        {
          hid: 'description',
          name: 'description',
          content: this.description,
        },
      ],
    };
  },
  methods: {
    getImageUrl(issue, year) {
      if (this.$i18n.locale === 'us') {
        year = year.replace('grade-', 'year-');
      }

      return '/images/landing/magazine/thumb-whats-buzzing-' + year + '-' + issue + '.jpg';
    },
  },
};
</script>

<style scoped lang="scss">
.section {
  header {
    margin-bottom: $base-padding * 2;
  }
}

.section-issues {
  li {
    padding: $base-padding 0;

    img {
      margin: 0 auto;
    }

    h3 {
      font-size: $font-size-700;
      margin: $base-padding 0;
    }

    p {
      margin: 0;
    }
  }
}
</style>
